import {
  Fragment,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
  type Ref,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import type { FollowUpView } from '@sigora/domain';

import type { SigoraUIMessage } from './events.ts';
import { Markdown } from './markdown.tsx';
import { Pending } from '../ui/Pending.tsx';

/**
 * How far above the bottom counts as having scrolled away. Below it the list is following the
 * conversation and every append and every streamed delta keeps the newest line in view; above it
 * the user is reading something and nothing moves under them.
 */
const FOLLOW_SLACK_PX = 80;

/**
 * The composer's contents, held outside the component that draws them.
 *
 * Two calls rather than a ref object, so the only thing a holder promises is that what was
 * written comes back — and so nothing on this side can reach past the words into whatever keeps
 * them.
 */
export interface DraftStore {
  read: () => string;
  write: (draft: string) => void;
}

/** What the conversation that draws this panel asks of it. */
export interface ChatPanelHandle {
  /**
   * The list follows the conversation from here, as it does after Send: for a message sent from
   * somewhere other than the composer, which the researcher wants to see next all the same.
   */
  follow: () => void;
}

export interface ChatPanelProps {
  ref?: Ref<ChatPanelHandle>;
  messages: readonly SigoraUIMessage[];
  status: 'submitted' | 'streaming' | 'ready' | 'error';
  /** The latest transient status part, which is what "building your guide…" comes from. */
  activity: string | null;
  error: Error | undefined;
  /** Whether the stored conversation is still on its way. */
  historyLoading: boolean;
  /** Why it never arrived, if it did not. Sending still works either way. */
  historyError: string | null;
  /**
   * What the composer asks for, which is also the panel's name: what this step's conversation is
   * for, in the product's words.
   */
  label: string;
  /**
   * Something about the study's conversation as a whole that bears on this one, such as a turn
   * running on another step. Shown in the drawer above the composer only, never on the closed tab.
   * Null when there is nothing to say.
   */
  notice: string | null;
  /**
   * Whether Send waits on something outside this conversation. While it does, Send is disabled,
   * Enter is taken and does nothing, and the words typed are kept; the notice, if there is one, is
   * what the composer and Send are described by. False when absent.
   */
  sendHeld?: boolean;
  /**
   * The user messages whose turn was cancelled, by message id. Session-only: the row itself stays
   * in the database and reads back as an ordinary message, because what was cancelled is the
   * reply, not the asking.
   */
  cancelledIds: ReadonlySet<string>;
  /**
   * Where the composer's contents are kept between mounts.
   *
   * This panel is drawn once per step's thread, so it is mounted and unmounted as the researcher
   * walks from step to step and cannot be what remembers a half-written message. The shell holds
   * each step's words; the composer reads them when it mounts and writes every change through.
   *
   * A store rather than a value and a callback, deliberately: what a researcher is typing is of no
   * interest to anything above this panel, and holding it as state up there would re-render the
   * step, the documents and the whole transcript on every keystroke.
   */
  draftStore: DraftStore;
  /**
   * Keep the conversation in a drawer at the screen's left edge, behind an "Ask Sigora" tab, until
   * the researcher opens it.
   */
  collapsible?: boolean;
  /**
   * Whether the drawer is open, for a holder that keeps it open beyond this mount; with
   * `onExpandedChange`, which every open and close goes through. Without them the drawer holds its
   * own state, starting closed.
   */
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  /**
   * A message to offer the composer, from a send that came back — cancelled, or refused while the
   * turn before it was still stopping. `null` when there is nothing to hand back.
   *
   * The token counts the offers, so the same words handed back twice are two offers rather than
   * one, and a render that passes an offer already answered is not a second chance to write over
   * the composer.
   *
   * Which offers this mount has answered is remembered here, and which offers *stand* is the
   * shell's: `onAnswered` fires whether the offer was taken up or declined, and the shell drops it.
   * Both are needed, because this panel's lifetime is shorter than the shell's — it is unmounted
   * whenever the researcher leaves its step — so the memory in here cannot be what keeps an answered offer from
   * being made again.
   */
  restore: {
    text: string;
    token: number;
    /**
     * Whether the offer answers a press somewhere else on the page, so the composer is brought to
     * the reader — focused and scrolled into view — even when it declines the words.
     */
    reveal?: boolean;
    /**
     * Whether words already in the composer take the offer after them, a blank line between,
     * rather than declining it: for words the researcher never had in the composer, which would
     * otherwise be lost.
     */
    append?: boolean;
    onTaken?: () => void;
    onAnswered?: () => void;
  } | null;
  onSend: (text: string) => void;
  /** Cancels the running turn. A conversation with nothing to cancel passes none, and shows no Cancel. */
  onStop?: () => void;
  /**
   * What the closed drawer's tab says when nothing more pressing is — a follow-up being answered, or one
   * waiting for a choice. Null when there is nothing to say.
   */
  closedNote?: string | null;
  /** Draws a Brief follow-up where a reply would be: its answer and the controls under it. */
  renderFollowUp?: (followUp: FollowUpView) => ReactNode;
  /** Draws what is said under one of the researcher's messages, by its id, or nothing. */
  renderUserNote?: (messageId: string) => ReactNode;
  /** Drawn in the composer above the words: what the next message is about, or nothing. */
  composerNote?: ReactNode;
}

const textOf = (message: SigoraUIMessage): string =>
  message.parts
    .filter((part) => part.type === 'text')
    .map((part) => part.text)
    .join('');

/**
 * One part at a time, by type, so a part this product starts showing later is a case added here
 * rather than a rewrite of the message body. A follow-up is drawn by whoever holds its controls.
 * Anything else renders as nothing: a tool part the client has no affordance for is not something
 * to show a user raw.
 */
const renderAssistantPart = (
  part: SigoraUIMessage['parts'][number],
  key: string,
  renderFollowUp: ((followUp: FollowUpView) => ReactNode) | undefined,
): ReactNode | null => {
  switch (part.type) {
    case 'text':
      return <Markdown key={key} text={part.text} />;
    case 'data-follow-up':
      return <Fragment key={key}>{renderFollowUp?.(part.data) ?? null}</Fragment>;
    default:
      return null;
  }
};

/**
 * The conversation. Every artifact the agent writes appears in the sidebar rather than here, so
 * this stays a conversation rather than becoming a document viewer with a chat attached.
 */
export const ChatPanel = ({
  ref,
  messages,
  status,
  activity,
  error,
  historyLoading,
  historyError,
  label,
  notice,
  sendHeld = false,
  cancelledIds,
  draftStore,
  collapsible = false,
  expanded: heldExpanded,
  onExpandedChange,
  restore,
  onSend,
  onStop,
  closedNote = null,
  renderFollowUp,
  renderUserNote,
  composerNote = null,
}: ChatPanelProps) => {
  /**
   * The words on screen, and the store they outlive this component in.
   *
   * Kept in both places at once: the state is what re-renders the composer as it is typed into,
   * and the store is what the next mount reads. The initialiser runs once per mount, which is
   * where a message written before a walk to another step comes back from.
   */
  const [draft, setDraft] = useState(() => draftStore.read());
  const [ownExpanded, setOwnExpanded] = useState(() => !collapsible);
  const expanded = heldExpanded ?? ownExpanded;
  const setExpanded = useCallback(
    (next: boolean) => {
      if (onExpandedChange === undefined) {
        setOwnExpanded(next);
      } else {
        onExpandedChange(next);
      }
    },
    [onExpandedChange],
  );
  const panelId = useId();
  const onDraftChange = useCallback(
    (next: string) => {
      draftStore.write(next);
      setDraft(next);
    },
    [draftStore],
  );
  const busy = status === 'submitted' || status === 'streaming';
  /** Whether Send does nothing now: this conversation's own turn is running, or Send is held. */
  const sendWaits = busy || sendHeld;
  const noticeId = `${panelId}-notice`;
  /** The line saying why Send is held, which the composer and Send are described by. */
  const heldBecause = sendHeld && notice !== null ? noticeId : undefined;

  const listRef = useRef<HTMLOListElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const focusInputOnOpen = useRef(false);
  const scrollInputOnOpen = useRef(false);
  const focusToggleOnClose = useRef(false);
  /** The last offer this composer answered, so it answers each one once. */
  const answeredOffer = useRef<number | null>(null);
  /**
   * Whether the list was following the conversation *before* this render added anything. It has to
   * be recorded on scroll rather than measured in the effect: by then the new content is already
   * laid out, so a list that was pinned to the bottom measures as far from it.
   */
  const following = useRef(true);

  useImperativeHandle(
    ref,
    () => ({
      follow: () => {
        following.current = true;
      },
    }),
    [],
  );

  const streamedLength = messages.reduce((total, message) => total + textOf(message).length, 0);
  const errorShown = error !== undefined;
  const closedStatus =
    error?.message ??
    (busy
      ? (activity ?? closedNote ?? 'Working…')
      : historyError !== null
        ? 'Conversation unavailable'
        : historyLoading
          ? 'Loading…'
          : (closedNote ?? (draft.trim() !== '' ? 'Draft saved' : null)));

  /**
   * Anything beside the list shortens it. The activity line, the error and the notice are flex
   * siblings of the scroll container, so each one arriving takes a line of height off it
   * without changing what it holds — the content the list was scrolled to the bottom of is suddenly
   * a line under the fold. That is why they are dependencies alongside the messages: on a sent turn
   * the activity line lands first and the next delta is seconds away, so without them the message
   * the user just sent sits out of sight for the whole thinking window.
   *
   * The two history lines are siblings on the same axis and count for the same reason, above the
   * list rather than below it though they are — a flex column gives the list what is left over,
   * wherever the line taking it sits. Both move without any message moving: the loading line
   * *unmounts* on a study whose transcript came back empty, and the error appears when no
   * transcript arrived at all, so neither shows up in the message dependencies.
   */
  useEffect(() => {
    const list = listRef.current;
    if (list === null || !following.current) {
      return;
    }

    list.scrollTop = list.scrollHeight;
  }, [
    // A follow-up's answer arriving changes a message's parts and neither count below.
    messages,
    messages.length,
    streamedLength,
    notice,
    busy,
    activity,
    errorShown,
    historyLoading,
    historyError,
    expanded,
  ]);

  /**
   * Takes up an offered message, and puts the cursor where it can be edited.
   *
   * Which sends come back, and with what, is the shell's to know — a turn nobody typed for has
   * nothing to give back, and a refusal knows which words the server did not keep. What belongs
   * here is the one rule about the composer itself: only into an empty one. Anything typed since
   * is newer than the message being offered and is not this component's to overwrite. An offer
   * that `append`s goes after those words instead, a blank line between, overwriting nothing.
   *
   * Empty means empty, not blank. A space or a newline is something the researcher put there, and
   * writing over it is the same overwrite as any other — that it cannot be sent as it stands is
   * what the Send button is for, not a reason to treat it as nothing.
   *
   * An offer declined changes nothing at all, the cursor included: the researcher is typing
   * somewhere, and moving them out of it is the same intrusion as overwriting their words. The one
   * exception is an offer that `reveal`s: it answers a control the researcher just pressed, so they
   * are already out of the composer, and bringing it to them — words untouched — is what shows the
   * press did something. An offer answered — accepted or declined — is answered for good, which is
   * what the token records.
   *
   * Whoever made the offer is told when it lands, and only then. What they do about it is theirs,
   * but it is a decision about where the words are: something that reads as putting them back
   * cannot be done while the composer has declined to hold them.
   */
  useEffect(() => {
    if (restore === null || restore.token === answeredOffer.current) {
      return;
    }

    answeredOffer.current = restore.token;
    const bringIntoView = () => {
      inputRef.current?.focus();
      // Optional because not every DOM implements it; focus alone still moves the reader there.
      inputRef.current?.scrollIntoView?.({ block: 'nearest' });
    };
    const revealComposer = (shouldScroll: boolean) => {
      if (!collapsible) {
        if (shouldScroll) {
          bringIntoView();
        } else {
          inputRef.current?.focus();
        }

        return;
      }

      focusInputOnOpen.current = true;
      scrollInputOnOpen.current = shouldScroll;
      setExpanded(true);
      if (expanded) {
        if (shouldScroll) {
          bringIntoView();
        } else {
          inputRef.current?.focus();
        }
        focusInputOnOpen.current = false;
        scrollInputOnOpen.current = false;
      }
    };

    if (draft !== '' && restore.append !== true) {
      if (restore.reveal === true) {
        revealComposer(true);
      }
      restore.onAnswered?.();

      return;
    }

    onDraftChange(draft === '' ? restore.text : `${draft}\n\n${restore.text}`);
    if (restore.reveal === true) {
      revealComposer(true);
    } else {
      revealComposer(false);
    }
    // Only now, because what the shell does about an offer being taken is bound up with the words
    // being somewhere: taking the sender's own copy off screen is right once the composer holds
    // them and wrong while it does not.
    restore.onTaken?.();
    restore.onAnswered?.();
  }, [restore, draft, onDraftChange, collapsible, expanded, setExpanded]);

  useEffect(() => {
    if (!expanded && focusToggleOnClose.current) {
      toggleRef.current?.focus();
      focusToggleOnClose.current = false;
    }
    if (!expanded || !focusInputOnOpen.current) {
      return;
    }

    inputRef.current?.focus();
    if (scrollInputOnOpen.current) {
      inputRef.current?.scrollIntoView?.({ block: 'nearest' });
    }
    focusInputOnOpen.current = false;
    scrollInputOnOpen.current = false;
  }, [expanded]);

  const onScroll = () => {
    const list = listRef.current;
    if (list === null) {
      return;
    }

    following.current = list.scrollHeight - list.scrollTop - list.clientHeight <= FOLLOW_SLACK_PX;
  };

  /**
   * Every path that hands a message to the agent goes through here. Sending is the user asking to
   * be at the bottom — their own message is what they want to see next, whatever they were reading
   * before.
   */
  const commit = (text: string) => {
    following.current = true;
    onSend(text);
  };

  const send = () => {
    const text = draft.trim();
    if (text === '' || sendWaits) {
      return;
    }

    onDraftChange('');
    commit(text);
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    send();
  };

  /**
   * Enter sends. On a blank draft or during this conversation's own turn it is an ordinary
   * newline. While Send is held it is taken and does nothing, so a message about to be sent is not
   * left with a stray line break in it.
   */
  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) {
      return;
    }

    if (sendHeld) {
      event.preventDefault();

      return;
    }

    if (busy || draft.trim() === '') {
      return;
    }

    event.preventDefault();
    send();
  };

  const chatContents = (
    <>
      {/*
        Above the transcript rather than below it, because what is still arriving is the part of the
        conversation that comes before everything already on screen.
      */}
      {historyLoading && (
        <p className="chat-activity" data-test-id="chat-history-loading">
          Loading the conversation…
        </p>
      )}

      {historyError !== null && (
        <p className="chat-error" data-test-id="chat-history-error" role="alert">
          {historyError}
        </p>
      )}

      <ol className="chat-messages" data-test-id="chat-messages" ref={listRef} onScroll={onScroll}>
        {messages.map((message) =>
          message.role === 'system' ? (
            // Something that happened rather than something somebody said — an approval, so far —
            // shown as a line between the messages with no speaker, because it has none.
            <li key={message.id} className="chat-message chat-message-system">
              <p className="chat-event" data-test-id="chat-message-system">
                {textOf(message)}
              </p>
            </li>
          ) : (
            <li key={message.id} className={`chat-message chat-message-${message.role}`}>
              <span className="chat-role">{message.role === 'user' ? 'You' : 'Sigora'}</span>
              {message.role === 'user' ? (
                <>
                  <p data-test-id="chat-message-user">{textOf(message)}</p>
                  {cancelledIds.has(message.id) && (
                    <p className="chat-cancelled" data-test-id="chat-cancelled">
                      Cancelled
                    </p>
                  )}
                  {renderUserNote?.(message.id)}
                </>
              ) : (
                <div className="chat-message-body" data-test-id="chat-message-assistant">
                  {message.parts.map((part, index) =>
                    renderAssistantPart(part, `${message.id}-${index}`, renderFollowUp),
                  )}
                </div>
              )}
            </li>
          ),
        )}
      </ol>

      {/*
        Transient, and only while the turn runs: it says what is happening now, which stops being
        true the moment the turn ends. It is deliberately not part of the transcript — a reply
        read back tomorrow should not still be announcing that it is thinking.
      */}
      {busy && activity !== null && (
        <Pending className="chat-activity" testId="chat-activity">
          {activity}
        </Pending>
      )}

      {/*
        Said as a status rather than an alert: it is not a failure of anything on this step, and
        the words in the composer are kept for when the turn behind it has finished. Only here,
        not on the closed tab: the tab speaks for this step's own conversation alone.
      */}
      {notice !== null && (
        <p className="chat-activity" data-test-id="chat-busy-notice" id={noticeId} role="status">
          {notice}
        </p>
      )}

      {error !== undefined && (
        <p className="chat-error" data-test-id="chat-error" role="alert">
          {error.message}
        </p>
      )}

      <form className="chat-composer" onSubmit={submit}>
        {composerNote}
        <label className="visually-hidden" htmlFor="chat-input">
          {label}
        </label>
        <textarea
          id="chat-input"
          data-test-id="chat-input"
          ref={inputRef}
          rows={3}
          value={draft}
          placeholder={label}
          onChange={(event) => onDraftChange(event.target.value)}
          onKeyDown={onKeyDown}
          aria-describedby={heldBecause}
        />
        <p className="artifact-meta" data-test-id="chat-composer-hint">
          Enter to send · Shift+Enter for a new line
        </p>
        <div className="chat-composer-actions">
          <button
            type="submit"
            data-test-id="chat-send"
            disabled={sendWaits || draft.trim() === ''}
            aria-describedby={heldBecause}
          >
            {status === 'submitted' ? 'Sending…' : 'Send'}
          </button>
          {/*
            Cancelling is explicit and separate from navigating away: closing the tab leaves the
            turn running, because the reply is still wanted and the work is already paid for.
          */}
          {busy && onStop !== undefined && (
            <button type="button" data-test-id="chat-stop" onClick={onStop}>
              Cancel
            </button>
          )}
        </div>
      </form>
    </>
  );

  if (!collapsible) {
    return (
      <section className="chat" data-test-id="chat-panel" aria-label={label}>
        {chatContents}
      </section>
    );
  }

  /*
    The tab and the drawer are fixed to the screen's left edge, under the header and above the step's
    footer, so the step stays readable and its Back and Continue reachable while the conversation is
    open. Not modal: nothing traps focus, and Escape inside the drawer closes it back to the tab.
  */
  return (
    <div
      className="chat-side"
      onKeyDown={(event) => {
        if (event.key === 'Escape' && !event.defaultPrevented && expanded) {
          event.preventDefault();
          focusToggleOnClose.current = true;
          setExpanded(false);
        }
      }}
    >
      <button
        ref={toggleRef}
        className="chat-tab"
        type="button"
        aria-expanded={expanded}
        aria-controls={panelId}
        data-test-id="chat-toggle"
        hidden={expanded}
        onClick={() => {
          if (!expanded) {
            focusInputOnOpen.current = true;
          }
          setExpanded(!expanded);
        }}
      >
        Ask Sigora
      </button>
      {!expanded && closedStatus !== null && (
        <span className="chat-tab-status" data-test-id="chat-toggle-status" role="status">
          {closedStatus}
        </span>
      )}
      <section
        id={panelId}
        className="chat chat-expanded chat-drawer"
        data-test-id="chat-panel"
        aria-label={label}
        hidden={!expanded}
      >
        <div className="chat-heading">
          <span>{label}</span>
          <button
            type="button"
            data-test-id="chat-collapse"
            onClick={() => {
              focusToggleOnClose.current = true;
              setExpanded(false);
            }}
          >
            Close
          </button>
        </div>
        {chatContents}
      </section>
    </div>
  );
};
